import { BASE_URL, AUTH_HEADERS } from "./config"

export const getChecklists = async (statusId) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists`, {
        headers: AUTH_HEADERS
    })
    if (response.ok) {
        return await response.json()
    }
    return []
}

export const getChecklist = async (statusId, checklistId) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}`, {
        headers: AUTH_HEADERS
    })
    if (response.ok) {
        return await response.json()
    }
    return {}
}

export const createChecklist = async (statusId, checklist) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists`, {
        method: "POST",
        headers: AUTH_HEADERS,
        body: JSON.stringify(checklist)
    })
    if (response.ok) {
        return await response.json()
    }
    return {}
}

export const updateChecklist = async (statusId, checklistId, checklist) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}`, {
        method: "PATCH",
        headers: AUTH_HEADERS,
        body: JSON.stringify(checklist)
    })
    if (response.ok) {
        return await response.json()
    }
    return {error: true}
}

export const deleteChecklist = async (statusId, checklistId) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}`, {
        method: "DELETE",
        headers: AUTH_HEADERS
    })
    if (response.ok) {
        return true
    }
    return false
}

export const getShowChecklistSettings = async (statusId, checklistId) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/show-settings`, {
        headers: AUTH_HEADERS
    })
    if (response.ok) {
        return await response.json()
    }
    return {}
}

export const updateShowChecklistSettings = async (statusId, checklistId, cfsSelected) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/show-settings-update`, {
        method: "POST",
        headers: AUTH_HEADERS,
        body: JSON.stringify({cfs_selected: cfsSelected})
    })
    if (response.ok) {
        return await response.json()
    }
    return {}
}

export const getOptionVisibility = async (statusId, checklistId) => {
    if (checklistId === undefined || checklistId === null) {
        return [];
    }
    try {
        const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/option-visibility`, {
            method: "GET",
            headers: AUTH_HEADERS
        });
        
        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            return [];
        }
    } catch (error) {
        return [];
    }
}

export const updateOptionVisibility = async (statusId, checklistId, customFieldId, optionValue) => {
    if (checklistId === undefined || checklistId === null || customFieldId === undefined || customFieldId === null) {
        return {};
    }
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/option-visibility-update`, {
        method: "POST",
        headers: AUTH_HEADERS,
        body: JSON.stringify({
            custom_field_id: customFieldId,
            option_value: optionValue
        })
    })
    if (response.ok) {
        return await response.json()
    }
    return {}
}

export const deleteOptionVisibility = async (statusId, checklistId, visibilityId) => {
    if (checklistId === undefined || checklistId === null || visibilityId === undefined || visibilityId === null) {
        return false;
    }
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/option-visibility-delete/${visibilityId}`, {
        method: "DELETE",
        headers: AUTH_HEADERS
    });
    return response.ok;
}

export const getHidedChecklistsBySettings = async (amoLeadId, statusId) => {
    const response = await fetch(`${BASE_URL}/hided-checklists-by-settings`, {
        method: "POST",
        headers: AUTH_HEADERS,
        body: JSON.stringify({amo_lead_id: amoLeadId, status_id: statusId})
    })
    if (response.ok) {
        return (await response.json()).checklists_hided
    }
    return []
}

export const createConditionalRequiredRule = async (statusId, checklistId, payload) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/conditional-required-rules`, {
        method: "POST",
        headers: AUTH_HEADERS,
        body: JSON.stringify(payload)
    })
    if (response.ok) {
        return await response.json()
    }
    return {}
}

export const deleteConditionalRequiredRule = async (statusId, checklistId, ruleId) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/conditional-required-rules/${ruleId}`, {
        method: "DELETE",
        headers: AUTH_HEADERS
    })
    return response.ok
}

/**
 * Создает настройку блокировки полей чеклиста по незаполненным AMO-полям.
 *
 * Функция является тонкой API-границей: принимает текущий AMO-статус, чеклист и
 * payload настроек, а применение правила к значениям карточки выполняется ниже
 * в frontend state и сервисах сохранения.
 */
export const createAmoPrerequisiteRule = async (statusId, checklistId, payload) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/amo-prerequisite-rules`, {
        method: "POST",
        headers: AUTH_HEADERS,
        body: JSON.stringify(payload)
    })
    if (response.ok) {
        return await response.json()
    }
    return {}
}

/**
 * Удаляет настройку блокировки AMO-предусловий для текущего чеклиста и статуса.
 *
 * Возвращает boolean, чтобы UI мог синхронизировать локальный список правил
 * только после подтвержденного удаления на backend.
 */
export const deleteAmoPrerequisiteRule = async (statusId, checklistId, ruleId) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/amo-prerequisite-rules/${ruleId}`, {
        method: "DELETE",
        headers: AUTH_HEADERS
    })
    return response.ok
}

export const updateChecklistPosition = async (statusId, checklistId, position) => {
    const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/position`, {
        method: "PATCH",
        headers: AUTH_HEADERS,
        body: JSON.stringify({ position })
    });
    if (response.ok) {
        return await response.json();
    }
    return { error: true };
}

export const copyChecklist = async (statusId, checklistId, targetStatusId) => {
    try {
        const response = await fetch(`${BASE_URL}/statuses/${statusId}/checklists/${checklistId}/copy`, {
            method: "POST",
            headers: AUTH_HEADERS,
            body: JSON.stringify({ target_status_id: targetStatusId })
        });
        
        if (response.ok) {
            const result = await response.json();
            return {
                success: true,
                data: result
            };
        } else {
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                error: errorData.detail || errorData.message || `HTTP ${response.status}`,
                status: response.status
            };
        }
    } catch (error) {
        return {
            success: false,
            error: error.message || "Ошибка сети при копировании чеклиста",
            status: 0
        };
    }
}
